/*************
 * User roles (single source of truth)
 */

export const USER_ROLE = {
  ADMIN: "admin",
  TEACHER: "teacher",
  USER: "user",
  COMMUNITY_MODERATOR: "community_moderator",
} as const;

export type UserRole = (typeof USER_ROLE)[keyof typeof USER_ROLE];

export const USER_ROLE_LABEL: Record<UserRole, string> = {
  [USER_ROLE.ADMIN]: "Quản trị",
  [USER_ROLE.TEACHER]: "Giáo viên",
  [USER_ROLE.USER]: "Người dùng",
  [USER_ROLE.COMMUNITY_MODERATOR]: "Kiểm duyệt cộng đồng",
};

export const ASSIGNABLE_USER_ROLES: UserRole[] = [
  USER_ROLE.ADMIN,
  USER_ROLE.TEACHER,
  USER_ROLE.USER,
  USER_ROLE.COMMUNITY_MODERATOR,
];

export const getRoleLabel = (role?: string): string =>
  USER_ROLE_LABEL[role as UserRole] ?? "Khác";

/** Roles allowed past the CMS password login. */
export const CMS_LOGIN_ROLES: string[] = [USER_ROLE.ADMIN, USER_ROLE.COMMUNITY_MODERATOR];

/**
 * Only RESTRICTED roles appear here. A role with no entry keeps today's
 * unrestricted access — which is what keeps admin/teacher/user untouched.
 */
const ROLE_ALLOWED_PATHS: Partial<Record<UserRole, string[]>> = {
  [USER_ROLE.COMMUNITY_MODERATOR]: ["/community"],
};

const ROLE_HOME_PATH: Partial<Record<UserRole, string>> = {
  [USER_ROLE.COMMUNITY_MODERATOR]: "/community",
};

export const canAccessPath = (role: string | undefined, pathname: string): boolean => {
  const allowed = ROLE_ALLOWED_PATHS[role as UserRole];
  if (!allowed) return true; // admin / teacher / user / role chưa xác định
  return allowed.some((base) => pathname === base || pathname.startsWith(`${base}/`));
};

export const getRoleHomePath = (role?: string): string =>
  ROLE_HOME_PATH[role as UserRole] ?? "/users";

/** Synchronous role hint so a hard refresh does not paint a forbidden page. */
export const ROLE_CACHE_KEY = "AJAX_USER_ROLE";

export const cacheRole = (role?: string) => {
  if (role) localStorage.setItem(ROLE_CACHE_KEY, role);
};

export const resolveRole = (account?: { user_role?: string }): string | undefined =>
  account?.user_role ?? localStorage.getItem(ROLE_CACHE_KEY) ?? undefined;
